import { useState, useRef, useEffect } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

const BACKEND = "https://gemini-backend-v2-production.up.railway.app";

const SUBJECT_CONFIG = [
  { id: "construction",    name: "Construction",    icon: "CN", color: "#f59e0b" },
  { id: "environmental",   name: "Environmental",   icon: "EV", color: "#10b981" },
  { id: "geotechnical",    name: "Geotechnical",    icon: "GT", color: "#8b5cf6" },
  { id: "structural",      name: "Structural",      icon: "ST", color: "#ef4444" },
  { id: "transportation",  name: "Transportation",  icon: "TR", color: "#3b82f6" },
  { id: "water_resources", name: "Water Resources", icon: "WR", color: "#06b6d4" },
];

const SYSTEM_PROMPT = `You are an AI Socratic tutor specializing in the NCEES Civil PE exam depth modules. Your primary objective is to guide experienced engineers through complex Civil PE problems using the Socratic method, fostering deep understanding and critical thinking, mirroring the professional judgment required on the exam.

Never provide direct solutions. Instead, prompt the user to articulate their reasoning, recall relevant principles, and apply appropriate methodologies. Acknowledge mathematically equivalent correct answers. Once a problem is fully and correctly solved, confirm with "Correct! You got it." Guide users through problems one logical step at a time. If a user is stuck, offer a precise, targeted hint that nudges them forward without giving away the next step. When an error is made, clearly identify the incorrect step or assumption and ask the user to re-evaluate their approach.

Keep your responses concise and professional, typically 2-4 sentences. Insist on proper engineering units for all numerical answers. If a user explicitly requests a full solution after multiple unsuccessful attempts, provide a detailed step-by-step walkthrough. Format all math using LaTeX: $x$ for inline, $$y=mx+b$$ for display. If a user agrees to a similar problem, generate one using the same Socratic method.

Emphasize ACI 318, ASCE 7, AISC 360, AASHTO, and the NCEES PE Civil Reference Handbook. Maintain a peer-to-peer professional tone — treat the user as a fellow experienced engineer.`;

const REFERENCE_SECTIONS = [
  { title: "Construction", icon: "CN", items: [
    { label: "PERT Expected Duration", formula: "$T_E = \\frac{T_O + 4T_M + T_P}{6}$" },
    { label: "PERT Activity Variance", formula: "$V = \\left(\\frac{T_P - T_O}{6}\\right)^2$" },
    { label: "Earned Value", formula: "$EV = \\% \\text{complete} \\times BAC$" },
    { label: "Cost Variance", formula: "$CV = EV - AC$" },
    { label: "CPI", formula: "$CPI = \\frac{EV}{AC}$" },
    { label: "SPI", formula: "$SPI = \\frac{EV}{PV}$" },
    { label: "Earthwork (Avg End Area)", formula: "$V = L \\frac{A_1 + A_2}{2}$" },
  ]},
  { title: "Environmental", icon: "EV", items: [
    { label: "BOD (First-Order)", formula: "$L_t = L_0 e^{-kt}$" },
    { label: "F/M Ratio", formula: "$F/M = \\frac{Q_0 S_0}{VX}$" },
    { label: "MCRT", formula: "$\\theta_c = \\frac{V_R X_R}{Q_w X_R + Q_e X_e}$" },
    { label: "Darcy's Law", formula: "$Q = -KA \\frac{dh}{dL}$" },
    { label: "Seepage Velocity", formula: "$v_s = \\frac{Ki}{n_e}$" },
    { label: "Ideal Gas Law", formula: "$PV = nRT$" },
  ]},
  { title: "Geotechnical", icon: "GT", items: [
    { label: "Effective Stress", formula: "$\\sigma' = \\sigma - u$" },
    { label: "Mohr-Coulomb", formula: "$\\tau_f = c' + \\sigma'_n \\tan \\phi'$" },
    { label: "Terzaghi Bearing Capacity", formula: "$q_{ult} = cN_c + qN_q + 0.5\\gamma B N_{\\gamma}$" },
    { label: "Consolidation (NC)", formula: "$S_c = \\frac{C_c H}{1+e_0} \\log_{10} \\left(\\frac{\\sigma'_f}{\\sigma'_i}\\right)$" },
    { label: "Rankine Ka", formula: "$K_a = \\tan^2\\left(45^\\circ - \\frac{\\phi'}{2}\\right)$" },
    { label: "Infinite Slope FS", formula: "$FS = \\frac{c' + (\\gamma H \\cos^2\\beta - u)\\tan\\phi'}{\\gamma H \\sin\\beta \\cos\\beta}$" },
  ]},
  { title: "Structural", icon: "ST", items: [
    { label: "Euler Buckling", formula: "$P_{cr} = \\frac{\\pi^2 EI}{(KL)^2}$" },
    { label: "Steel Mp", formula: "$M_p = F_y Z_x$" },
    { label: "Concrete Mn (Whitney)", formula: "$M_n = A_s f_y \\left(d - \\frac{a}{2}\\right)$" },
    { label: "Concrete Vc", formula: "$V_c = 2\\lambda\\sqrt{f'_c}\\, b_w d$" },
    { label: "ASCE 7 Base Shear", formula: "$V = C_s W$" },
    { label: "LRFD Load Combo", formula: "$U = 1.2D + 1.6L$" },
  ]},
  { title: "Transportation", icon: "TR", items: [
    { label: "Stopping Sight Distance", formula: "$SSD = 1.47Vt + \\frac{V^2}{30(a/g \\pm G)}$" },
    { label: "Superelevation", formula: "$e + f_s = \\frac{V^2}{15R}$" },
    { label: "Pavement SN", formula: "$SN = a_1 D_1 + a_2 D_2 m_2 + a_3 D_3 m_3$" },
    { label: "Flow-Speed-Density", formula: "$Q = VK$" },
    { label: "Crest Curve Length", formula: "$L = \\frac{AS^2}{200(\\sqrt{h_1}+\\sqrt{h_2})^2}$" },
    { label: "HCM Lane Capacity", formula: "$c = s \\frac{g}{C}$" },
  ]},
  { title: "Water Resources", icon: "WR", items: [
    { label: "Manning's (US)", formula: "$Q = \\frac{1.49}{n} A R^{2/3} S^{1/2}$" },
    { label: "Bernoulli + Head Loss", formula: "$\\frac{P_1}{\\gamma}+\\frac{V_1^2}{2g}+z_1+H_P-h_L = \\frac{P_2}{\\gamma}+\\frac{V_2^2}{2g}+z_2$" },
    { label: "Rational Method", formula: "$Q = CiA$" },
    { label: "SCS Runoff", formula: "$Q = \\frac{(P-0.2S)^2}{P+0.8S},\\; S = \\frac{1000}{CN}-10$" },
    { label: "Pump Power", formula: "$P = \\frac{\\gamma Q H_p}{\\eta}$" },
    { label: "Froude Number", formula: "$Fr = \\frac{V}{\\sqrt{gy}}$" },
  ]},
  { title: "Constants & Units", icon: "CU", items: [
    { label: "g (US)", formula: "$g = 32.2\\,\\text{ft/s}^2$" },
    { label: "g (SI)", formula: "$g = 9.81\\,\\text{m/s}^2$" },
    { label: "gamma_w (US)", formula: "$\\gamma_w = 62.4\\,\\text{lbf/ft}^3$" },
    { label: "E_steel", formula: "$E_s = 29{,}000\\,\\text{ksi}$" },
    { label: "E_concrete (ACI)", formula: "$E_c = 57{,}000\\sqrt{f'_c}\\,\\text{psi}$" },
  ]},
];

const C = {
  bg: "#080d17", panel: "#0e1623", border: "#182639",
  text: "#dde6f0", muted: "#3d5570",
  accent: "#4ade80", accent2: "#a7f3d0", green: "#10b981",
};

function genUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

function getProblemId(problem) {
  if (!problem) return null;
  if (problem.diagram_image)
    return problem.diagram_image.split("/").pop().replace(/\.[^.]+$/, "");
  // Composite key: subject_id + chapter + first 40 chars of question
  const subj = problem.subject_id || "unknown";
  const chap = problem.chapter || "ch0";
  const qKey = (problem.q || "").replace(/\s+/g, " ").trim().slice(0, 40);
  return `${subj}::${chap}::${qKey}`;
}

// Ã¢â€â‚¬Ã¢â€â‚¬ PROGRESS HELPERS Ã¢â€â‚¬Ã¢â€â‚¬
const PROGRESS_KEY = "progress_v1";
function getProgress() {
  try {
    const p = JSON.parse(localStorage.getItem(PROGRESS_KEY)) || { solvedProblems: [], visitedChapters: [], visitedSubjects: [] };
    // Migration: strip null/invalid IDs saved by old buggy getProblemId
    const cleaned = p.solvedProblems.filter(id => id != null && id !== "null" && String(id).trim() !== "");
    if (cleaned.length !== p.solvedProblems.length) {
      p.solvedProblems = cleaned;
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
    }
    return p;
  }
  catch { return { solvedProblems: [], visitedChapters: [], visitedSubjects: [] }; }
}
function saveProgress(p) { localStorage.setItem(PROGRESS_KEY, JSON.stringify(p)); }
function markProblemSolved(id) {
  if (!id) return;
  const p = getProgress();
  if (!p.solvedProblems.includes(id)) { p.solvedProblems.push(id); saveProgress(p); }
}
function markChapterVisited(id) {
  if (!id) return;
  const p = getProgress();
  if (!p.visitedChapters.includes(id)) { p.visitedChapters.push(id); saveProgress(p); }
}
function markSubjectVisited(id) {
  if (!id) return;
  const p = getProgress();
  if (!p.visitedSubjects.includes(id)) { p.visitedSubjects.push(id); saveProgress(p); }
}

function renderMath(text) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(/(\$\$[\s\S]+?\$\$|\$[^\$\n]+?\$)/g);
  return parts.map((part, i) => {
    try {
      if (part.startsWith("$$") && part.endsWith("$$"))
        return <span key={i} dangerouslySetInnerHTML={{ __html: katex.renderToString(part.slice(2,-2), { throwOnError: false, displayMode: true, output: "html" }) }} />;
      if (part.startsWith("$") && part.endsWith("$"))
        return <span key={i} dangerouslySetInnerHTML={{ __html: katex.renderToString(part.slice(1,-1), { throwOnError: false, displayMode: false, output: "html" }) }} />;
    } catch { return <span key={i}>{part}</span>; }
    return <span key={i}>{part}</span>;
  });
}

function buildSubject(config, problems) {
  const chapterMap = {}, chapterOrder = [];
  problems.forEach(p => {
    const ch = p.chapter || "General";
    if (!chapterMap[ch]) { chapterMap[ch] = []; chapterOrder.push(ch); }
    chapterMap[ch].push(p);
  });
  return {
    ...config,
    chapters: chapterOrder.map(ch => ({
      id: ch.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
      name: ch,
      problems: chapterMap[ch],
    })),
  };
}

function ImageModal({ src, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "#fff", fontSize: 28, cursor: "pointer" }}>✕</button>
      <img src={src} alt="" style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} onClick={e => e.stopPropagation()} />
    </div>
  );
}

// Ã¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢ÂÃ¢â€¢Â
export default function CePePrepApp() {
  const [screen, setScreen]                 = useState("loading");
  const [subjects, setSubjects]             = useState([]);
  const [subjectsLoaded, setSubjectsLoaded] = useState(false);
  const [subjectsError, setSubjectsError]   = useState(false);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);

  const [guidelineMap, setGuidelineMap] = useState({});
  const [comicsMap, setComicsMap]       = useState({});

  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [solved, setSolved]       = useState(false);
  const [attempts, setAttempts]   = useState(0);
  const [chatTab, setChatTab]     = useState("tutor");
  const [modalImg, setModalImg]   = useState(null);

  const [deviceId, setDeviceId]           = useState(null);
  const [license, setLicense]             = useState(null);
  const [daysLeft, setDaysLeft]           = useState(null);
  const [regName, setRegName]             = useState("");
  const [regEmail, setRegEmail]           = useState("");
  const [regLoading, setRegLoading]       = useState(false);
  const [regError, setRegError]           = useState("");
  const [renewalSent, setRenewalSent]     = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [openSection, setOpenSection]     = useState(null);
  const [progress, setProgress]           = useState(() => getProgress());

  const refreshProgress = () => setProgress(getProgress());

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const hasInitialized = useRef(false);
  const pinchRef = useRef(null);
  const [comicScale, setComicScale] = useState(1);
  const [comicTranslate, setComicTranslate] = useState({ x: 0, y: 0 });
  const [figureScale, setFigureScale] = useState(1);
  const [figureTranslate, setFigureTranslate] = useState({ x: 0, y: 0 });
  const [sketchScale, setSketchScale] = useState(1);
  const [sketchTranslate, setSketchTranslate] = useState({ x: 0, y: 0 });

  const currentSubject = subjects.find(s => s.id === selectedSubject) || null;

  // Load subjects
  useEffect(() => {
    (async () => {
      try {
        const results = await Promise.all(
          SUBJECT_CONFIG.map(async cfg => {
            const res = await fetch(`/pb/${cfg.id}.json`);
            if (!res.ok) throw new Error(`Failed: ${cfg.id}`);
            return buildSubject(cfg, await res.json());
          })
        );
        setSubjects(results);
        setSubjectsLoaded(true);
      } catch (e) {
        console.error(e);
        setSubjectsError(true);
        setSubjectsLoaded(true);
      }
    })();
  }, []);

  // Load guidelines + comics for a subject on demand
  const loadSubjectData = async (subjectId) => {
    if (!guidelineMap[subjectId]) {
      try {
        const res = await fetch(`/pb/guidelines/${subjectId}.json`);
        if (res.ok) {
          const arr = await res.json();
          const map = {};
          arr.forEach(g => { map[g.problem_id] = g; });
          setGuidelineMap(prev => ({ ...prev, [subjectId]: map }));
        }
      } catch {}
    }
    if (!comicsMap[subjectId]) {
      try {
        const res = await fetch(`/comics/${subjectId}/concepts.json`);
        if (res.ok) {
          const data = await res.json();
          setComicsMap(prev => ({ ...prev, [subjectId]: Array.isArray(data) ? data : (data.value || []) }));
        }
      } catch {}
    }
  };

  // License init
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    (async () => {
      // Free web access — no registration required
      setScreen("home");
    })();
  }, []);

  // Deep link
  useEffect(() => {
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const l = await CapApp.addListener("appUrlOpen", async ({ url }) => {
          if (!url?.includes("renewal-success")) return;
          const did = localStorage.getItem("deviceId"); if (!did) return;
          setScreen("loading");
          try {
            const res = await fetch(`${BACKEND}/api/check-license`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: did, app_type: "ce_pe" }) });
            const d = await res.json();
            if (d.valid === true || d.valid === "true") { setLicense(d); if (d.expires_at) setDaysLeft(Math.ceil((new Date(d.expires_at) - new Date()) / 86400000)); setScreen("home"); }
            else setScreen("expired");
          } catch { setScreen("no_wifi"); }
        });
        return () => l.remove();
      } catch {}
    })();
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const saveLrsReport = async (msgs, problem) => {
    if (localStorage.getItem("privacy_accepted") !== "true") return;
    const clean = (msgs || []).filter(m => m.content && !m.loading);
    if (clean.length < 2) return;
    try {
      await fetch(`${BACKEND}/api/save-lrs`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: localStorage.getItem("deviceId"), user_name: localStorage.getItem("userName"), user_email: localStorage.getItem("userEmail"), app_name: "CE PE", problem_topic: problem?.topic, problem_text: problem?.q, message_count: clean.length, messages: clean }), keepalive: true });
    } catch {}
  };

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") saveLrsReport(messages, selectedProblem); };
    const onUnload = () => saveLrsReport(messages, selectedProblem);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("beforeunload", onUnload); };
  }, [messages, selectedProblem]);

  const callGemini = async (msgs, sys) => {
    // Free web access — skip license check
    let vm = msgs.filter(m => m.content != null);
    while (vm.length > 0 && vm[0].role === "assistant") vm = vm.slice(1);
    if (!vm.length) throw new Error("No messages");
    const res = await fetch(`${BACKEND}/api/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ system: sys, messages: vm }) });
    const d = await res.json();
    if (d.error) throw new Error(d.error);
    return d.reply || "";
  };

  const handleRegister = async () => {
    if (!regName.trim() || !regEmail.trim()) { setRegError("Please enter your name and email."); return; }
    if (!/\S+@\S+\.\S+/.test(regEmail)) { setRegError("Please enter a valid email address."); return; }
    setRegLoading(true); setRegError("");
    try {
      const did = localStorage.getItem("deviceId") || genUUID();
      localStorage.setItem("deviceId", did);
      const res = await fetch(`${BACKEND}/api/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: did, name: regName.trim(), email: regEmail.trim(), app_type: "ce_pe" }) });
      const data = await res.json();
      if (data.registered) {
        localStorage.setItem("userName", regName.trim()); localStorage.setItem("userEmail", regEmail.trim());
        setLicense({ valid: data.is_active, expires_at: data.expires_at });
        if (data.expires_at) setDaysLeft(Math.ceil((new Date(data.expires_at) - new Date()) / 86400000));
        setScreen("home");
      } else setRegError("Registration failed. Please try again.");
    } catch { setRegError("Please check your internet connection."); }
    setRegLoading(false);
  };

  const handleRenewalRequest = async () => {
    try {
      await fetch(`${BACKEND}/api/request-renewal`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: localStorage.getItem("deviceId"), email: localStorage.getItem("userEmail"), name: localStorage.getItem("userName") }) });
      setRenewalSent(true);
    } catch {}
  };

  const startProblem = async (problem) => {
    const ch = currentSubject?.chapters.find(c => c.id === selectedChapter);
    const enriched = { ...problem, subject_id: currentSubject?.id, subject_name: currentSubject?.name, chapter_id: ch?.id, chapter_name: ch?.name };
    setSelectedProblem(enriched); setSolved(false); setAttempts(0); setInput(""); setChatTab("tutor");
    setScreen("chat");
    setMessages([{ role: "assistant", content: null, loading: true }]); setLoading(true);
    const sys = `${SYSTEM_PROMPT}\n\nCurrent Problem: "${problem.q}"\nCorrect Answer (do NOT reveal): ${problem.answer}\nTopic: ${problem.topic}`;
    try {
      const reply = await callGemini([{ role: "user", content: `I'm ready to work on this problem: "${problem.q}"` }], sys);
      setMessages([{ role: "assistant", content: reply }]);
    } catch { setMessages([{ role: "assistant", content: "Let's work through this together. What formula or concept applies here?" }]); }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim(); setInput("");
    const na = attempts + 1; setAttempts(na);
    const updated = [...messages, { role: "user", content: userText }];
    setMessages([...updated, { role: "assistant", content: null, loading: true }]); setLoading(true);
    const sys = `${SYSTEM_PROMPT}\n\nCurrent Problem: "${selectedProblem.q}"\nCorrect Answer (do NOT reveal until earned): ${selectedProblem.answer}\nTopic: ${selectedProblem.topic}\nAttempt: ${na}`;
    try {
      const history = updated.filter(m => m.content != null).map(m => ({ role: m.role, content: m.content }));
      const msgHist = history.length > 0 && history[0].role === "assistant" ? [{ role: "user", content: `Problem: "${selectedProblem.q}"` }, ...history] : history;
      const reply = await callGemini(msgHist, sys);
      if (/\bCorrect! You got it\b|That is the correct final answer/i.test(reply)) { setSolved(true); markProblemSolved(getProblemId(selectedProblem)); refreshProgress(); saveLrsReport([...updated, { role: "assistant", content: reply }], selectedProblem); }
      setMessages([...updated, { role: "assistant", content: reply }]);
    } catch (err) {
      const msg = err.message === "EXPIRED" ? "AI Tutor subscription has expired." : "Connection error — please try again.";
      setMessages([...updated, { role: "assistant", content: msg }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const GS = `@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;700&display=swap');
    *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
    html,body{border:none!important;outline:none!important;background:${C.bg}!important;margin:0;padding:0;}
    #root{border:none!important;outline:none!important;background:${C.bg}!important;margin:0 auto!important;padding:0;max-width:1126px;width:100%;display:flex;flex-direction:column;min-height:100svh;box-sizing:border-box;text-align:center;}
    ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:${C.bg}}::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
    textarea:focus{outline:none;border-color:${C.accent}!important}
    @keyframes blink{0%,100%{opacity:.2}50%{opacity:1}}
    @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}`;

  const wrap = (children, noPad) => (
    <div style={{ fontFamily: "'DM Mono',monospace", background: C.bg, minHeight: "100vh", color: C.text, border: "none", outline: "none", maxWidth: 460, margin: "0 auto" }}>
      <style>{GS}</style>
      {children}
    </div>
  );

  // Ã¢â€â‚¬Ã¢â€â‚¬ LOADING Ã¢â€â‚¬Ã¢â€â‚¬
  if (screen === "loading") return (
    <div style={{ fontFamily: "'DM Mono',monospace", background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: 48, filter: `drop-shadow(0 0 20px ${C.accent})` }}>⚙</div>
      <div style={{ fontSize: 12, color: C.accent2, letterSpacing: "0.2em" }}>LOADING...</div>
    </div>
  );

  // Ã¢â€â‚¬Ã¢â€â‚¬ NO WIFI Ã¢â€â‚¬Ã¢â€â‚¬
  if (screen === "no_wifi") return wrap(
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "40px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📶</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>No Internet Connection</div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.8, marginBottom: 28 }}>Internet connection is required to use the AI Tutor.</div>
      <button onClick={() => {
        const did = localStorage.getItem("deviceId"); setScreen("loading");
        (async () => {
          try {
            const res = await fetch(`${BACKEND}/api/check-license`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: did, app_type: "ce_pe" }) });
            const data = await res.json();
            const isReg = data.registered === true || data.registered === "true" || data.valid === true || data.valid === "true";
            if (isReg) { setLicense(data); if (data.expires_at) setDaysLeft(Math.ceil((new Date(data.expires_at) - new Date()) / 86400000)); setScreen("home"); }
            else setScreen("register");
          } catch { setScreen("no_wifi"); }
        })();
      }} style={{ background: C.accent, border: "none", borderRadius: 8, padding: "12px 24px", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Retry</button>
    </div>
  );

  // Ã¢â€â‚¬Ã¢â€â‚¬ PRIVACY Ã¢â€â‚¬Ã¢â€â‚¬
  if (screen === "privacy") return wrap(
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <div style={{ background: C.panel, padding: "18px 20px", borderBottom: `1px solid ${C.border}`, textAlign: "center" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: C.accent, fontWeight: 700 }}>PRIVACY POLICY</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Please review before continuing</div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px", fontSize: 11, lineHeight: 1.9, color: C.muted }}>
        <p><b style={{ color: C.accent2 }}>Effective Date: March 8, 2026</b></p>
        <p style={{ color: C.text, fontWeight: 600 }}>To receive personalized AI tutoring, your consent is required.</p>
        <p><b style={{ color: C.accent2 }}>1. Information We Collect</b><br />Name, email, learning experience data (problem-solving patterns, time-on-task, mastery of concepts, AI tutor interactions).</p>
        <p><b style={{ color: C.accent2 }}>2. How We Use Your Data</b><br />Generate personalized learning paths, optimize content, and track competency progress.</p>
        <p><b style={{ color: C.accent2 }}>3. Data Storage and Security</b><br />Data processed on Railway, stored in Supabase with AES-256 encryption and SSL.</p>
        <p><b style={{ color: C.accent2 }}>4. Your Rights</b><br />Right to access, export, and delete your data at any time.</p>
        <p><b style={{ color: C.accent2 }}>5. Third-Party Disclosure</b><br />We do not sell your data. Anonymized data may be shared with AI partners to improve tutoring.</p>
        <p><b style={{ color: C.accent2 }}>6. License Term</b><br />App license is active for 3 months from registration. Renewal required for continued access.</p>
      </div>
      <div style={{ background: C.panel, padding: "14px 20px", borderTop: `1px solid ${C.border}` }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, cursor: "pointer" }}>
          <input type="checkbox" checked={privacyChecked} onChange={e => setPrivacyChecked(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, accentColor: C.accent }} />
          <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>I have read and agree to the Privacy Policy and consent to data collection for personalized AI learning.</span>
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => { localStorage.setItem("privacy_accepted", "false"); setScreen("register"); }}
            style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={() => { if (!privacyChecked) return; localStorage.setItem("privacy_accepted", "true"); setScreen("register"); }}
            disabled={!privacyChecked}
            style={{ flex: 2, padding: "12px", background: privacyChecked ? C.accent : C.border, border: "none", borderRadius: 8, color: privacyChecked ? "#fff" : C.muted, fontSize: 13, fontWeight: 700, cursor: privacyChecked ? "pointer" : "not-allowed", fontFamily: "inherit" }}>Accept &amp; Continue</button>
        </div>
      </div>
    </div>
  );

  if (screen === "register") return wrap(
    <div style={{ padding: "40px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "0.04em" }}>CIVIL PE PREP</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 4, letterSpacing: "0.15em" }}>PE EXAM - CIVIL ENGINEERING</div>
      </div>
      <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 12, padding: "24px 20px", marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: C.accent2, letterSpacing: "0.15em", marginBottom: 16, fontWeight: 700 }}>FREE REGISTRATION</div>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.8, marginBottom: 20 }}>AI Tutor is free for 3 months after registration.</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>NAME *</div>
          <input value={regName} onChange={e => setRegName(e.target.value)} placeholder="Enter your name"
            style={{ width: "100%", background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>EMAIL *</div>
          <input value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="Enter your email" type="email"
            style={{ width: "100%", background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
        </div>
        {regError && <div style={{ fontSize: 11, color: "#ef4444", marginBottom: 14 }}>{regError}</div>}
        <button onClick={handleRegister} disabled={regLoading}
          style={{ width: "100%", background: C.accent, border: "none", borderRadius: 8, padding: "12px", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {regLoading ? "Registering..." : "Get Started"}
        </button>
      </div>
    </div>
  );

  // EXPIRED
  if (screen === "expired") return wrap(
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32, textAlign: "center" }}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>!</div>
      <div style={{ color: "#ef4444", fontSize: 20, fontWeight: 700, marginBottom: 12 }}>License Has Expired</div>
      <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 32, maxWidth: 300 }}>Renew for $9.99 to continue. Secure payment via Stripe.</div>
      <button onClick={async () => {
        try {
          const res = await fetch(BACKEND + "/api/create-checkout", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: deviceId, email: localStorage.getItem("userEmail"), app_type: "ce_pe" }) });
          const d = await res.json();
          if (d.url) window.open(d.url, "_blank");
        } catch { alert("Network error."); }
      }} style={{ background: C.accent, color: "#fff", borderRadius: 10, padding: "14px 32px", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", marginBottom: 16 }}>Renew Now - $9.99</button>
      <button onClick={() => {
        setScreen("loading");
        (async () => {
          try {
            const res = await fetch(BACKEND + "/api/check-license", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: deviceId, app_type: "ce_pe" }) });
            const d = await res.json();
            if (d.valid === true || d.valid === "true") { setLicense(d); if (d.expires_at) setDaysLeft(Math.ceil((new Date(d.expires_at) - new Date()) / 86400000)); setScreen("home"); }
            else setScreen("expired");
          } catch { setScreen("no_wifi"); }
        })();
      }} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Already renewed? Tap to refresh</button>
    </div>
  );

  // REFERENCE
  if (screen === "reference") return wrap(
    <div>
      <div style={{ background: C.panel, borderBottom: "1px solid " + C.border, padding: "13px 18px", position: "sticky", top: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => setScreen("home")} style={{ background: "none", border: "none", color: C.accent2, fontSize: 15, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 3, fontWeight: 500 }}><span style={{ fontSize: 20, lineHeight: 1 }}>‹</span> Back</button>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", color: C.accent, fontWeight: 700, textTransform: "uppercase" }}>PE Reference</div>
          <div style={{ fontSize: 10, color: C.muted }}>Civil PE Reference Handbook</div>
        </div>
      </div>
      <div style={{ padding: "16px" }}>
        {REFERENCE_SECTIONS.map((section, si) => (
          <div key={si} style={{ marginBottom: 10 }}>
            <div onClick={() => setOpenSection(openSection === si ? null : si)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.panel, border: "1px solid " + C.border, borderRadius: openSection === si ? "8px 8px 0 0" : 8, padding: "12px 16px", cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: C.accent2, fontSize: 13, fontWeight: 700 }}>{section.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{section.title}</span>
              </div>
              <span style={{ color: C.muted }}>{openSection === si ? "^" : "v"}</span>
            </div>
            {openSection === si && (
              <div style={{ background: C.bg, border: "1px solid " + C.border, borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                {section.items.map((item, ii) => (
                  <div key={ii} style={{ padding: "10px 16px", borderBottom: ii < section.items.length - 1 ? "1px solid " + C.border : "none" }}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: C.accent2 }}>{renderMath(item.formula)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // COMIC VIEWER
  if (screen === "comic_viewer" && selectedConcept) {
    const subjId = selectedConcept.subject_id;
    const concept = selectedConcept.concept;
    return wrap(
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <div style={{ background: C.panel, borderBottom: "1px solid " + C.border, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setScreen("subject")} style={{ background: "none", border: "none", color: C.accent2, fontSize: 15, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 3, fontWeight: 500 }}><span style={{ fontSize: 20, lineHeight: 1 }}>‹</span> Back</button>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{concept.display_name}</div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          <div onTouchStart={e=>{if(e.touches.length===2){const dx=e.touches[0].clientX-e.touches[1].clientX,dy=e.touches[0].clientY-e.touches[1].clientY;pinchRef.current={dist:Math.hypot(dx,dy),scale:comicScale,isPinch:true};}else{const now=Date.now();if(pinchRef.current&&pinchRef.current.lastTap&&now-pinchRef.current.lastTap<300){setComicScale(1);setComicTranslate({x:0,y:0});}pinchRef.current={...pinchRef.current,lastTap:now,isPinch:false,panStartX:e.touches[0].clientX,panStartY:e.touches[0].clientY,panTx:comicTranslate.x,panTy:comicTranslate.y};}} } onTouchMove={e=>{if(e.touches.length===2&&pinchRef.current&&pinchRef.current.dist){e.preventDefault();const dx=e.touches[0].clientX-e.touches[1].clientX,dy=e.touches[0].clientY-e.touches[1].clientY;setComicScale(Math.min(4,Math.max(1,pinchRef.current.scale*(Math.hypot(dx,dy)/pinchRef.current.dist))));}else if(e.touches.length===1&&pinchRef.current&&!pinchRef.current.isPinch&&comicScale>1){e.preventDefault();setComicTranslate({x:pinchRef.current.panTx+e.touches[0].clientX-pinchRef.current.panStartX,y:pinchRef.current.panTy+e.touches[0].clientY-pinchRef.current.panStartY});} }} onTouchEnd={()=>{if(pinchRef.current){pinchRef.current.dist=null;pinchRef.current.isPinch=false;}}} style={{overflow:"hidden",borderRadius:10,background:"#fff",marginBottom:16,touchAction:"none"}}>
            <img src={"/comics/" + subjId + "/" + concept.concept_name + ".png"} alt={concept.display_name}
              style={{ width:"100%", display:"block", transformOrigin:"center center", transform:"scale("+comicScale+") translate("+(comicTranslate.x/comicScale)+"px,"+(comicTranslate.y/comicScale)+"px)", transition:"transform 0.05s ease" }}
              onError={e => { e.target.style.display = "none"; }} />
          </div>
          {comicScale > 1 && <button onClick={()=>{setComicScale(1);setComicTranslate({x:0,y:0});}} style={{background:"none",border:"none",color:C.muted,fontSize:10,cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>Reset zoom</button>}
          {concept.core_idea && (
            <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.accent2, letterSpacing: "0.15em", marginBottom: 8, fontWeight: 700 }}>CORE IDEA</div>
              <div style={{ fontSize: 12, color: C.text, lineHeight: 1.75 }}>{concept.core_idea}</div>
            </div>
          )}
          {concept.visual_hook && (
            <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 10, color: C.accent2, letterSpacing: "0.15em", marginBottom: 8, fontWeight: 700 }}>VISUAL HOOK</div>
              <div style={{ fontSize: 12, color: C.text, lineHeight: 1.75 }}>{concept.visual_hook}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // SUBJECT screen (concepts + chapters)
  if (screen === "subject" && currentSubject) {
    const concepts = comicsMap[currentSubject.id] || [];
    const subColor = currentSubject.color;
    return wrap(
      <div>
        <div style={{ background: C.panel, borderBottom: "1px solid " + C.border, padding: "13px 18px", position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setScreen("home")} style={{ background: "none", border: "none", color: C.accent2, fontSize: 15, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 3, fontWeight: 500 }}><span style={{ fontSize: 20, lineHeight: 1 }}>‹</span> Back</button>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.2em", color: subColor, fontWeight: 700, textTransform: "uppercase" }}>{currentSubject.icon} {currentSubject.name}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                {concepts.length} concepts - {currentSubject.chapters.length} chapters
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "16px 16px 40px" }}>
          {concepts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Concept Lessons</div>
              {concepts.map((concept, i) => (
                <div key={i} onClick={() => { setSelectedConcept({ subject_id: currentSubject.id, concept }); setScreen("comic_viewer"); }}
                  style={{ display: "flex", alignItems: "center", background: C.panel, border: "1px solid " + subColor + "22", borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: subColor + "20", border: "1px solid " + subColor + "40", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 18, color: subColor }}>
                    {"\u261D"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{concept.display_name}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.4, overflow: "hidden", maxHeight: 32 }}>{concept.core_idea}</div>
                  </div>
                  <div style={{ color: subColor, fontSize: 16, flexShrink: 0 }}>{">"}</div>
                </div>
              ))}

              <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
            </div>
          )}
          <div style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Chapters</div>
          {currentSubject.chapters.map(ch => (
            <div key={ch.id} onClick={() => { setSelectedChapter(ch.id); markChapterVisited(ch.id); refreshProgress(); setScreen("problems"); }}
              style={{ background: C.panel, border: "1px solid " + subColor + "22", borderRadius: 9, padding: "15px 17px", marginBottom: 9, cursor: "pointer" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{ch.name}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{ch.problems.length} problems</div>
            </div>
          ))}

        </div>
      </div>
    );
  }

  // PROBLEMS
  if (screen === "problems" && selectedChapter && currentSubject) {
    const ch = currentSubject.chapters.find(c => c.id === selectedChapter);
    const subColor = currentSubject.color;
    return wrap(
      <div>
        <div style={{ background: C.panel, borderBottom: "1px solid " + C.border, padding: "13px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setScreen("subject")} style={{ background: "none", border: "none", color: C.accent2, fontSize: 15, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 3, fontWeight: 500 }}><span style={{ fontSize: 20, lineHeight: 1 }}>‹</span> Back</button>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.2em", color: subColor, fontWeight: 700, textTransform: "uppercase" }}>{currentSubject.icon} {currentSubject.name}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{ch && ch.name}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: "16px 16px 40px" }}>
          {ch && ch.problems.map((p, i) => {
            const diffColor = p.difficulty === "hard" ? "#ef4444" : p.difficulty === "medium" ? "#10b981" : "#3b82f6";
            return (
              <div key={i} onClick={() => startProblem(p)}
                style={{ background: C.panel, border: "1px solid " + subColor + "22", borderRadius: 9, padding: "13px 14px", marginBottom: 9, cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ fontSize: 10, color: subColor, fontWeight: 700, marginTop: 2, flexShrink: 0, width: 22 }}>P{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, lineHeight: 1.6, color: C.text, overflow: "hidden", maxHeight: 60 }}>{renderMath(p.q)}</div>
                  <div style={{ fontSize: 9, color: C.text, marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span>{p.topic}</span>
                    <span style={{ fontSize: 8, padding: "1px 7px", borderRadius: 10, fontWeight: 700, background: diffColor + "20", color: diffColor, border: "1px solid " + diffColor + "40" }}>{p.difficulty && p.difficulty.toUpperCase()}</span>
                  </div>
                </div>
                <div style={{ color: subColor, fontSize: 16, marginTop: 2, flexShrink: 0 }}>{">"}</div>
              </div>
            );
          })}

        </div>
      </div>
    );
  }

  // CHAT
  if (screen === "chat" && selectedProblem) {
    const subColor = currentSubject ? currentSubject.color : C.accent;
    const problemId = getProblemId(selectedProblem);
    const subGuidelines = selectedProblem.subject_id ? guidelineMap[selectedProblem.subject_id] : null;
    const guideline = problemId && subGuidelines ? subGuidelines[problemId] : null;
    const figureSrc = selectedProblem.diagram_image ? "/" + selectedProblem.diagram_image : null;
    const sketchSrc = problemId && selectedProblem.subject_id ? "/sketches/" + selectedProblem.subject_id + "/" + problemId + ".png" : null;
    return (
      <div style={{ fontFamily: "'DM Mono',monospace", background: C.bg, minHeight: "100vh", color: C.text, display: "flex", flexDirection: "column" }}>
        <style>{GS}</style>
        {modalImg && <ImageModal src={modalImg} onClose={() => setModalImg(null)} />}
        <div style={{ background: C.panel, borderBottom: "1px solid " + C.border, padding: "12px 16px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setScreen("problems")} style={{ background: "none", border: "none", color: C.accent2, cursor: "pointer", fontSize: 15, padding: 0, display: "flex", alignItems: "center", gap: 3, fontWeight: 500 }}><span style={{ fontSize: 20, lineHeight: 1 }}>‹</span> Back</button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.2em", color: subColor, fontWeight: 700, textTransform: "uppercase" }}>{currentSubject && currentSubject.name} - {selectedProblem.topic}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>SOCRATIC AI TUTOR - PE EXAM</div>
            </div>
            {solved && <div style={{ fontSize: 9, color: C.green, border: "1px solid #065f46", borderRadius: 20, padding: "3px 10px" }}>SOLVED</div>}
          </div>
        </div>
        <div style={{ background: subColor + "10", borderBottom: "1px solid " + subColor + "25", padding: "12px 16px", flexShrink: 0 }}>
          {figureSrc && (<>
            <div onTouchStart={e=>{if(e.touches.length===2){const dx=e.touches[0].clientX-e.touches[1].clientX,dy=e.touches[0].clientY-e.touches[1].clientY;pinchRef.current={...pinchRef.current,figDist:Math.hypot(dx,dy),figScale:figureScale,isFigPinch:true};}else{const now=Date.now();if(pinchRef.current&&pinchRef.current.figLastTap&&now-pinchRef.current.figLastTap<300){setFigureScale(1);setFigureTranslate({x:0,y:0});}else if(figureScale===1){setModalImg(figureSrc);}pinchRef.current={...pinchRef.current,figLastTap:now,isFigPinch:false,figPanStartX:e.touches[0].clientX,figPanStartY:e.touches[0].clientY,figPanTx:figureTranslate.x,figPanTy:figureTranslate.y};}}} onTouchMove={e=>{if(e.touches.length===2&&pinchRef.current&&pinchRef.current.figDist){e.preventDefault();const dx=e.touches[0].clientX-e.touches[1].clientX,dy=e.touches[0].clientY-e.touches[1].clientY;setFigureScale(Math.min(4,Math.max(1,pinchRef.current.figScale*(Math.hypot(dx,dy)/pinchRef.current.figDist))));}else if(e.touches.length===1&&pinchRef.current&&!pinchRef.current.isFigPinch&&figureScale>1){e.preventDefault();setFigureTranslate({x:pinchRef.current.figPanTx+e.touches[0].clientX-pinchRef.current.figPanStartX,y:pinchRef.current.figPanTy+e.touches[0].clientY-pinchRef.current.figPanStartY});}}} onTouchEnd={()=>{if(pinchRef.current){pinchRef.current.figDist=null;pinchRef.current.isFigPinch=false;}}} style={{overflow:"hidden",borderRadius:8,background:"#fff",padding:6,marginBottom:10,touchAction:"none"}}>
              <img src={figureSrc} alt="Problem figure"
                style={{ width:"100%", display:"block", transformOrigin:"center center", transform:"scale("+figureScale+") translate("+(figureTranslate.x/figureScale)+"px,"+(figureTranslate.y/figureScale)+"px)", transition:"transform 0.05s ease" }}
                onError={e => { e.target.style.display = "none"; }} />
            </div>
            {figureScale > 1 && <button onClick={()=>{setFigureScale(1);setFigureTranslate({x:0,y:0});}} style={{background:"none",border:"none",color:C.muted,fontSize:10,cursor:"pointer",fontFamily:"inherit",marginBottom:4}}>Reset zoom</button>}

          </>)}
          <div style={{ fontSize: 12.5, lineHeight: 1.7, color: C.text, maxHeight: "15vh", overflowY: "auto" }}>{renderMath(selectedProblem.q)}</div>
        </div>
        <div style={{ display: "flex", background: C.panel, borderBottom: "1px solid " + C.border, flexShrink: 0 }}>
          {[["tutor", "AI TUTOR"], ["howto", "HOW TO START"]].map(([tab, label]) => (
            <button key={tab} onClick={() => setChatTab(tab)}
              style={{ flex: 1, padding: "10px 0", background: "none", border: "none", borderBottom: chatTab === tab ? "2px solid " + C.accent : "2px solid transparent", color: chatTab === tab ? C.accent2 : C.muted, fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", cursor: "pointer", fontFamily: "inherit" }}>
              {label}
            </button>
          ))}
        </div>
        {chatTab === "howto" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
            {guideline ? (
              <div>
                <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 4 }}>PROBLEM TYPE</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.accent2 }}>{guideline.problem_type}</div>
                  {guideline.code_ref && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{guideline.code_ref}</div>}
                </div>
                {guideline.formulas && guideline.formulas.length > 0 && (
                  <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 10 }}>KEY FORMULAS</div>
                    {guideline.formulas.map((f, i) => (
                      <div key={i} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{f.label}</div>
                        <div style={{ fontSize: 13, color: C.text }}>{renderMath(f.expr)}</div>
                      </div>
                    ))}
                  </div>
                )}
                {guideline.steps && guideline.steps.length > 0 && (
                  <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 10 }}>ATTACK STEPS</div>
                    {guideline.steps.map((s, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 10, color: C.accent2, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{renderMath(s)}</div>
                      </div>
                    ))}
                  </div>
                )}
                {guideline.mistakes && guideline.mistakes.length > 0 && (
                  <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: C.accent2, letterSpacing: "0.15em", marginBottom: 10 }}>COMMON MISTAKES</div>
                    {guideline.mistakes.map((m, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                        <div style={{ fontSize: 12, color: "#ef4444", flexShrink: 0 }}>!</div>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{renderMath(m)}</div>
                      </div>
                    ))}
                  </div>
                )}
                {sketchSrc && (<>
                  <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 8 }}>REFERENCE SKETCH</div>
                  <div onTouchStart={e=>{if(e.touches.length===2){const dx=e.touches[0].clientX-e.touches[1].clientX,dy=e.touches[0].clientY-e.touches[1].clientY;pinchRef.current={...pinchRef.current,skDist:Math.hypot(dx,dy),skScale:sketchScale,isSkPinch:true};}else{const now=Date.now();if(pinchRef.current&&pinchRef.current.skLastTap&&now-pinchRef.current.skLastTap<300){setSketchScale(1);setSketchTranslate({x:0,y:0});}else if(sketchScale===1){setModalImg(sketchSrc);}pinchRef.current={...pinchRef.current,skLastTap:now,isSkPinch:false,skPanStartX:e.touches[0].clientX,skPanStartY:e.touches[0].clientY,skPanTx:sketchTranslate.x,skPanTy:sketchTranslate.y};}}} onTouchMove={e=>{if(e.touches.length===2&&pinchRef.current&&pinchRef.current.skDist){e.preventDefault();const dx=e.touches[0].clientX-e.touches[1].clientX,dy=e.touches[0].clientY-e.touches[1].clientY;setSketchScale(Math.min(4,Math.max(1,pinchRef.current.skScale*(Math.hypot(dx,dy)/pinchRef.current.skDist))));}else if(e.touches.length===1&&pinchRef.current&&!pinchRef.current.isSkPinch&&sketchScale>1){e.preventDefault();setSketchTranslate({x:pinchRef.current.skPanTx+e.touches[0].clientX-pinchRef.current.skPanStartX,y:pinchRef.current.skPanTy+e.touches[0].clientY-pinchRef.current.skPanStartY});}}} onTouchEnd={()=>{if(pinchRef.current){pinchRef.current.skDist=null;pinchRef.current.isSkPinch=false;}}} style={{overflow:"hidden",borderRadius:8,background:"#fff",marginBottom:10,touchAction:"none"}}>
                    <img src={sketchSrc} alt="sketch"
                      style={{ width:"100%", display:"block", transformOrigin:"center center", transform:"scale("+sketchScale+") translate("+(sketchTranslate.x/sketchScale)+"px,"+(sketchTranslate.y/sketchScale)+"px)", transition:"transform 0.05s ease" }}
                      onError={e => { e.target.style.display = "none"; }} />
                  </div>
                  {sketchScale > 1 && <button onClick={()=>{setSketchScale(1);setSketchTranslate({x:0,y:0});}} style={{background:"none",border:"none",color:C.muted,fontSize:10,cursor:"pointer",fontFamily:"inherit",marginBottom:4}}>Reset zoom</button>}
                </>)}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted, fontSize: 12 }}>No guideline available for this problem.</div>
            )}
          </div>
        )}
        {chatTab === "tutor" && (
          <>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px 6px", display: "flex", flexDirection: "column" }}>
              {messages.map((m, i) => (
                <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 10, animation: "fadeIn .25s ease" }}>
                  {m.role === "assistant" && (
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: C.accent + "20", border: "1px solid " + C.accent + "50", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0, marginRight: 8, marginTop: 2, color: C.accent2 }}>PE</div>
                  )}
                  <div style={{ maxWidth: "82%", background: m.role === "user" ? C.accent + "20" : C.panel, border: "1px solid " + (m.role === "user" ? C.accent + "50" : C.border), borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", padding: "10px 13px", fontSize: 12.5, lineHeight: 1.72, color: m.role === "user" ? C.accent2 : C.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {m.loading ? (
                      <span>{[0, 0.2, 0.4].map((d, idx) => <span key={idx} style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: C.accent2, margin: "0 2px", animation: "blink 1.2s " + d + "s infinite" }} />)}</span>
                    ) : renderMath(m.content)}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {solved && (
              <div style={{ padding: "10px 14px", background: "#071a10", borderTop: "1px solid #065f46", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <div style={{ flex: 1, fontSize: 11, color: C.green }}>You solved it! Great work.</div>
                <button onClick={() => setScreen("problems")} style={{ background: C.green, border: "none", borderRadius: 7, padding: "8px 16px", color: "#022c22", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Next</button>
              </div>
            )}
            <div style={{ padding: "10px 12px 14px", background: C.panel, borderTop: "1px solid " + C.border, display: "flex", gap: 8, flexShrink: 0 }}>
              <textarea ref={inputRef} rows={2} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
                placeholder="Show your work, type an answer, or ask for a hint..."
                style={{ flex: 1, background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "9px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", resize: "none", lineHeight: 1.5 }} />
              <button onClick={sendMessage} disabled={!input.trim() || loading}
                style={{ background: !input.trim() || loading ? C.border : C.accent, border: "none", borderRadius: 8, padding: "9px 14px", color: !input.trim() || loading ? C.muted : "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", alignSelf: "stretch" }}>SEND</button>
            </div>
          </>
        )}
      </div>
    );
  }

  // HOME — DASHBOARD
  const totalProblems = subjects.reduce((a, s) => a + s.chapters.reduce((b, c) => b + c.problems.length, 0), 0);
  const totalChapters = subjects.reduce((a, s) => a + s.chapters.length, 0);
  const solvedCount   = progress.solvedProblems.length;
  const chapDoneCount = progress.visitedChapters.length;
  const subjDoneCount = progress.visitedSubjects.length;

  return wrap(
    <div>
      {/* Ã¢â€â‚¬Ã¢â€â‚¬ HEADER Ã¢â€â‚¬Ã¢â€â‚¬ */}
      <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: "13px 18px", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.22em", color: C.accent, fontWeight: 700, textTransform: "uppercase" }}>CIVIL PE PREP</div>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.12em", marginTop: 2 }}>Socratic AI Tutor</div>
          </div>
          <button onClick={() => setScreen("reference")}
            style={{ background: `${C.accent}20`, border: `1px solid ${C.accent}50`, borderRadius: 8, padding: "6px 12px", color: C.accent2, fontSize: 10, fontWeight: 700, cursor: "pointer", letterSpacing: "0.1em", fontFamily: "inherit" }}>
            HANDBOOK
          </button>
        </div>
      </div>

      <div style={{ padding: "20px 16px 48px" }}>

        {/* Ã¢â€â‚¬Ã¢â€â‚¬ EXPIRY BANNER Ã¢â€â‚¬Ã¢â€â‚¬ */}
        {daysLeft !== null && daysLeft <= 7 && daysLeft > 0 && (
          <div style={{ background: `${C.accent}15`, border: `1px solid ${C.accent}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: C.accent2, fontWeight: 700 }}>{"\u26a0"} AI Tutor expires in {daysLeft} days</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>Would you like to continue?</div>
            </div>
            {renewalSent ? (
              <div style={{ fontSize: 10, color: C.green }}>{"\u2713"} Request Sent</div>
            ) : (
              <button onClick={handleRenewalRequest} style={{ background: C.accent, border: "none", borderRadius: 6, padding: "7px 12px", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" }}>Renew</button>
            )}
          </div>
        )}

        {/* Ã¢â€â‚¬Ã¢â€â‚¬ STATS CARD Ã¢â€â‚¬Ã¢â€â‚¬ */}
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 16px", marginBottom: 20 }}>
          <div style={{ fontSize: 9, letterSpacing: "0.22em", color: C.muted, textTransform: "uppercase", marginBottom: 14 }}>Your Progress</div>
          <div style={{ display: "flex", gap: 10 }}>
            {[
              { value: subjDoneCount,  total: subjects.length || 6, label: "Subjects Active" },
              { value: chapDoneCount,  total: totalChapters,        label: "Chapters" },
              { value: solvedCount,    total: totalProblems,        label: "Problems Solved" },
            ].map(({ value, total, label }) => (
              <div key={label} style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "13px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.accent2, lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 8, color: C.muted, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 5, lineHeight: 1.4 }}>{label}</div>
                <div style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>/ {total}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Ã¢â€â‚¬Ã¢â€â‚¬ HOW IT WORKS Ã¢â€â‚¬Ã¢â€â‚¬ */}
        <div style={{ background: `${C.accent}10`, border: `1px solid ${C.accent}25`, borderRadius: 12, padding: "16px", marginBottom: 20 }}>
          <div style={{ fontSize: 9, letterSpacing: "0.22em", color: C.accent2, fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>How It Works</div>
          {[
            { n: "1", t: "Select Module",  d: "Pick a subject and chapter to study" },
            { n: "2", t: "Chat with AI",   d: "Solve problems guided by Socratic tutor" },
            { n: "3", t: "Master Exam",    d: "Build deep understanding, problem by problem" },
          ].map(({ n, t, d }) => (
            <div key={n} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${C.accent}30`, border: `1px solid ${C.accent}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: C.accent2, flexShrink: 0 }}>{n}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{d}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Ã¢â€â‚¬Ã¢â€â‚¬ SUBJECT MODULES Ã¢â€â‚¬Ã¢â€â‚¬ */}
        {subjectsError && (
          <div style={{ background: "#3b1a1a", border: "1px solid #ef4444", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 12, color: "#ef4444" }}>
            Failed to load problem bank. Please check your connection.
          </div>
        )}


        <div style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 12 }}>PE Depth Modules</div>
        {!subjectsLoaded ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: C.muted, fontSize: 12 }}>Loading subjects...</div>
        ) : (
          subjects.map(sub => {
            const subTotal  = sub.chapters.reduce((a, c) => a + c.problems.length, 0);
            const subSolved = progress.solvedProblems.filter(id =>
              sub.chapters.some(ch => ch.problems.some(p => getProblemId(p) === id))
            ).length;
            const pct     = subTotal > 0 ? Math.round((subSolved / subTotal) * 100) : 0;
            const visited = progress.visitedSubjects.includes(sub.id);
            return (
              <div key={sub.id} onClick={() => { setSelectedSubject(sub.id); loadSubjectData(sub.id); markSubjectVisited(sub.id); refreshProgress(); setScreen("subject"); }}
                style={{ background: C.panel, border: `1px solid ${sub.color}${visited ? "55" : "22"}`, borderRadius: 12, padding: "14px 15px", marginBottom: 10, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: pct > 0 ? 10 : 0 }}>
                  <div style={{ width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: `${sub.color}18`, border: `1px solid ${sub.color}40`, color: sub.color, fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                    {sub.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{sub.name}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                      {sub.chapters.length} chapters &middot; {subTotal} problems
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: pct > 0 ? sub.color : C.muted }}>{pct}%</div>
                    <div style={{ fontSize: 9, color: C.muted }}>{subSolved}/{subTotal}</div>
                  </div>
                </div>
                {pct > 0 && (
                  <div style={{ height: 4, background: `${sub.color}20`, borderRadius: 4 }}>
                    <div style={{ height: 4, width: `${pct}%`, background: sub.color, borderRadius: 4, transition: "width 0.4s ease" }} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
